// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import styles from './index.module.scss';
import { Modal } from 'components/atoms/Modal';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconCloseTablet } from 'components/atoms/icons/IconCloseTablet';
import {
  OptionItem,
  SelectFieldMultipleChoise,
} from '../SelectFieldMultipleChoise';
import CheckboxField from 'components/atoms/CheckboxField';
import IconShipping from 'components/atoms/icons/IconShipping';
import {
  getCollectRouteItems,
  getRoutesCollectedTotalWeight,
} from 'apis/collect_route';
import { CollectRouteSiteItem } from 'types/routeManagementTypes';
import { deliveries } from 'apis/collects';
import { toast } from 'react-toastify';
import { normalizeNumber } from 'util/commons';
import Popover from 'components/atoms/Popover';
import { getCommonCollectRoutes } from 'apis/commons';

interface Props {
  onClose: () => void;
  deletingName?: string;
  content?: string | null;
  closeModalIcon?: React.ReactNode;
}

const LoadingVolumeModal: React.FC<Props> = ({ onClose, closeModalIcon }) => {
  const { t } = useTranslation();
  const [currentRouteItems, setCurrentRouteItems] = useState<
    CollectRouteSiteItem[]
  >([]);
  const [checkedIds, setCheckedIds] = useState<number[]>([]);
  const [routeOptions, setRouteOptions] = useState<OptionItem[]>([]);
  const [filter, setFilter] = useState<number[]>([]);
  const isSelected = (itemId: number) => checkedIds.includes(itemId);

  const fetchRouteItems = (routeIds: number[]) => {
    getCollectRouteItems({
      routeIds,
      successCallback: setCurrentRouteItems,
      failedCallback: () => {
        setCurrentRouteItems([]);
      },
    });
  };

  useEffect(() => {
    getCommonCollectRoutes({
      successCallback: (data) => {
        setRouteOptions(
          data.map(({ id, routeName }) => ({ id, name: routeName }))
        );
      },
      failedCallback: () => setRouteOptions([]),
    });
  }, []);

  useEffect(() => {
    fetchRouteItems(filter);
  }, [filter]);

  useEffect(() => {
    setCheckedIds([]);
  }, [currentRouteItems]);

  const overallTotalWeight = useMemo(
    () =>
      currentRouteItems
        .filter((item) => isSelected(item.wasteUnitId))
        .reduce((sum, item) => {
          return sum + Number(item.totalWeight);
        }, 0),
    [currentRouteItems, checkedIds]
  );

  const handleSelectItem = (itemId: number) => {
    setCheckedIds((prev) => {
      if (isSelected(itemId)) {
        return checkedIds.filter((id) => id != itemId);
      }

      return [...prev, itemId];
    });
  };

  const handleSelectAllItems = () => {
    if (checkedIds.length < currentRouteItems.length) {
      setCheckedIds(currentRouteItems.map(({ wasteUnitId }) => wasteUnitId));
    } else {
      setCheckedIds([]);
    }
  };

  const handleDeliveries = () =>
    deliveries({
      deliveryIds: currentRouteItems.flatMap((item) => {
        return isSelected(item.wasteUnitId) ? item.deliveryIds : [];
      }),
      successCallback: () => {
        toast.success(t('messages.M_088'));
        getRoutesCollectedTotalWeight();

        if (checkedIds.length === currentRouteItems.length) {
          onClose();
          return;
        }

        fetchRouteItems(filter);
        setCheckedIds([]);
      },
    });

  return (
    <div className={styles.customModal}>
      <Modal>
        <div className={styles.loadingVolumeModal}>
          <div className={styles.header}>
            <div className={styles.title}>
              {t('loading_volume_modal.title')}
            </div>

            {closeModalIcon ?? <IconCloseTablet onClick={onClose} />}
          </div>

          <div className={styles.body}>
            <div className={styles.actionBar}>
              <div className={styles.filter}>
                <SelectFieldMultipleChoise
                  options={routeOptions}
                  name="Items"
                  searchable={true}
                  placeholder={
                    t('loading_volume_modal.filter_placeholder') ?? ''
                  }
                  onChange={(items) => {
                    setFilter(items.map(({ id }) => Number(id)));
                  }}
                  selectedOptions={filter}
                  darkMode
                  allowClear
                />
              </div>

              <div className={styles.selectAll}>
                <CheckboxField
                  title={t('loading_volume_modal.select_all')}
                  input={{
                    checked:
                      checkedIds.length > 0 &&
                      checkedIds.length === currentRouteItems.length,
                    onChange: () => handleSelectAllItems(),
                  }}
                />
              </div>
            </div>

            <div className={styles.items}>
              {currentRouteItems.length ? (
                currentRouteItems.map((item) => (
                  <div key={item.wasteUnitId} className={styles.itemCard}>
                    <div className={styles.itemInfo}>
                      <div className={styles.selectItem}>
                        <CheckboxField
                          input={{
                            id: String(item.wasteUnitId),
                            checked: isSelected(item.wasteUnitId),
                            onChange: () => handleSelectItem(item.wasteUnitId),
                          }}
                          size="large"
                        />
                        <div className={styles.itemName}>
                          <Popover
                            autoGen={{
                              lines: 2,
                              odd: 4,
                              wrapperStyle: {
                                width: 'fit-content',
                              },
                            }}
                            popUpStyle={{
                              fontSize: 16,
                            }}
                            positions={['top']}
                            content={item.wasteUnitName}
                            containerStyle={{
                              zIndex: '100000',
                            }}
                          >
                            <span>{item.wasteUnitName}</span>
                          </Popover>
                        </div>
                      </div>

                      <div className={styles.cardRight}>
                        <div className={styles.overall}>
                          <div className={styles.quantity}>
                            <div className={styles.quantityTitle}>
                              {t('loading_volume_modal.overall.quantity')}
                            </div>
                            <div className={styles.quantityContent}>
                              <div className={styles.quantityNumber}>
                                <Popover
                                  autoGen={{
                                    odd: 4,
                                    wrapperStyle: {
                                      width: 'fit-content',
                                    },
                                  }}
                                  popUpStyle={{
                                    fontSize: 16,
                                  }}
                                  positions={['top']}
                                  content={normalizeNumber({
                                    value: item.emissionsCount,
                                  })}
                                  containerStyle={{
                                    zIndex: '100000',
                                  }}
                                >
                                  <span>
                                    {normalizeNumber({
                                      value: item.emissionsCount,
                                    })}
                                  </span>
                                </Popover>
                              </div>
                              <div className={styles.unit}>
                                {t(
                                  'loading_volume_modal.overall.quantity_unit'
                                )}
                              </div>
                            </div>
                          </div>
                          <div className={styles.weight}>
                            <div className={styles.weightTitle}>
                              {t('loading_volume_modal.overall.weight')}
                            </div>
                            <div className={styles.weightContent}>
                              <div className={styles.weightNumber}>
                                <Popover
                                  autoGen={{
                                    odd: 4,
                                    wrapperStyle: {
                                      width: 'fit-content',
                                    },
                                  }}
                                  popUpStyle={{
                                    fontSize: 16,
                                  }}
                                  positions={['top']}
                                  content={normalizeNumber({
                                    value: item.totalWeight,
                                  })}
                                  containerStyle={{
                                    zIndex: '100000',
                                  }}
                                >
                                  <span>
                                    {normalizeNumber({
                                      value: item.totalWeight,
                                    })}
                                  </span>
                                </Popover>
                              </div>
                              <div className={styles.unit}>kg</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.empty}>
                  <IconShipping size={52} strokeColor="#4d4d4d" />
                  <div className={styles.emptyText}>
                    {t('loading_volume_modal.empty')}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={styles.bottom}>
            <div className={styles.bottomContent}>
              <div className={styles.overall}>
                <div className={styles.quantity}>
                  <div className={styles.quantityTitle}>
                    {t('loading_volume_modal.overall.selected_count')}
                  </div>
                  <div className={styles.quantityContent}>
                    <div className={styles.quantityNumber}>
                      <Popover
                        autoGen={{
                          odd: 4,
                          wrapperStyle: {
                            width: 'fit-content',
                          },
                        }}
                        popUpStyle={{
                          fontSize: 16,
                        }}
                        positions={['top']}
                        content={checkedIds.length}
                        containerStyle={{
                          zIndex: '100000',
                        }}
                      >
                        <span className={styles.siteAddress}>
                          {checkedIds.length}
                        </span>
                      </Popover>
                    </div>
                    <div className={styles.unit}>
                      {t('loading_volume_modal.overall.selected_count_unit')}
                    </div>
                  </div>
                </div>
                <div className={styles.weight}>
                  <div className={styles.weightTitle}>
                    {t('loading_volume_modal.overall.total_weight')}
                  </div>
                  <div className={styles.weightContent}>
                    <div className={styles.weightNumber}>
                      <Popover
                        autoGen={{
                          odd: 4,
                          wrapperStyle: {
                            width: 'fit-content',
                          },
                        }}
                        popUpStyle={{
                          fontSize: 16,
                        }}
                        positions={['top']}
                        content={
                          normalizeNumber({
                            value: overallTotalWeight,
                          }) || 0
                        }
                        containerStyle={{
                          zIndex: '100000',
                        }}
                      >
                        <span className={styles.siteAddress}>
                          {normalizeNumber({
                            value: overallTotalWeight,
                          }) || 0}
                        </span>
                      </Popover>
                    </div>
                    <div className={styles.unit}>kg</div>
                  </div>
                </div>
              </div>

              <button
                className={styles.submitButton}
                onClick={() => handleDeliveries()}
                disabled={checkedIds.length === 0}
              >
                {t('loading_volume_modal.submit_button')}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LoadingVolumeModal;
